import { Component } from '@angular/core';
import { TimeRange } from '@nrwl/common-lib';
import { get, merge } from 'lodash';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'nrwl-summary-chart-base',
  template: '<p>summary-chart-base works!</p>'
})
export class SummaryChartBaseComponent {
  widgetConfig;
  notifyZoomEventListeners: Function; // eslint-disable-line
  notifyPinEventListeners: Function; // eslint-disable-line
  title;
  timeRangeValue;
  nextValues: Map<string, any>; // eslint-disable-line
  chartRangeOverride = {
    softMin: undefined,
    softMax: undefined
  };
  activeTestFilterParams;
  chartTickValueInMinutes;

  isChartsZoomed = false;
  chartControls = {
    widgets: []
  };
  groupPadding = 0.2;

  fromTime: any; // eslint-disable-line
  toTime: any; // eslint-disable-line

  chartSeries = [];
  isLoading = false;

  maxAllowedGap = 1;
  aggregationRange = 60000;

  constructor() {
    this.defaultOnInit();
  }

  public defaultOnInit(): void {
    // an entry point to load the data into this widget
    this.notifyZoomEventListeners = this.zoomEventBubblesUp.bind(this);
    this.chartSeries = get(this.widgetConfig, 'options.series', []);
  }

  public defaultOnDestroy(): void {
    this.chartRangeOverride = {
      softMin: undefined,
      softMax: undefined
    };
  }

  // eslint-disable-next-line
  getConversionUnit(i): any {
    return get(this.widgetConfig[i], 'options.conversionUnits', []);
  }

  // eslint-disable-next-line
  getDisplayUnit(i): any {
    return get(this.widgetConfig[i], 'options.displayUnit', 'value');
  }

  public getKpiUnit(i: number): string {
    return get(this.widgetConfig[i], `options.timeUnit`, 'milliseconds');
  }

  // eslint-disable-next-line
  getSingleValueChartOptions(index: number): any {
    const config = this.widgetConfig[index];
    const options = {
      chart: {
        height: get(config, 'options.height', 'auto'),
        styledMode: false,
        zoomType: 'x'
      },
      plotOptions: {
        series: {
          turboThreshold: 12000, //Increasing to 12000 per MV-26881
          states: {
            hover: {
              enabled: true
            }
          }
        }
      },
      xAxis: {
        visible: true,
        type: 'datetime',
        dateTimeLabelFormats: {
          minute: {
            main: '%H:%M'
          },
          hour: {
            main: '%H:%M'
          },
          day: {
            main: '%e. %b'
          },
          week: {
            main: '%e. %b'
          },
          month: {
            main: "%b '%y"
          }
        }
      },
      legend: {
        enabled: get(config, 'options.showInLegend', true),
        align: 'center',
        verticalAlign: 'bottom',
        useHTML: true,
        symbolWidth: 7,
        symbolHeight: 7,
        symbolRadius: 0,
        itemStyle: {
          fontWeight: 'normal',
          fontSize: '8pt'
        }
      },
      tooltip: {
        shared: true
      }
    };

    const overrideOptions = get(this.widgetConfig[index].options, 'override', {});
    // merge them with options from above, giving priority to 'override' options from json
    const mergedOptions = merge(options, overrideOptions, {
      xAxis: this.chartRangeOverride
    });
    // retrieve ALL options, currently defined for widget with id '${key}'
    const fullOptions = get(this.widgetConfig[index], 'options', {});
    // insert prepared options
    fullOptions.override = mergedOptions;
    return fullOptions;
  }
  /**
   * A top level listener for zoom events.
   * Catches notification from any of child controls
   * and propagates the event to all controls on the current page.
   */
  // eslint-disable-next-line
  public zoomEventBubblesUp(event: any): void {
    if (event.state === 'reset' || event.stat === 'zoom-out') {
      this.isChartsZoomed = false;
    } else if (event.state === 'zoom-in') {
      this.isChartsZoomed = true;
    }
  }

  public resetZoomLevel(): void {
    this.chartControls.widgets.forEach(widget => {
      widget.resetZoomLevel();
    });
  }

  public setTimeRangeValue(timeRange: TimeRange): void {
    if (timeRange.timerange) {
      this.chartRangeOverride.softMax = new Date().getTime();
      this.chartRangeOverride.softMin = this.chartRangeOverride.softMax - timeRange.timerange;
    } else {
      this.chartRangeOverride.softMin = timeRange.fromTime;
      this.chartRangeOverride.softMax = timeRange.toTime;
    }
  }

  // eslint-disable-next-line
  public calculateChartTickValue(): void {
    this.chartTickValueInMinutes = 1;
  }

  // eslint-disable-next-line
  public _handleGap(item: any, granularity = 1): void {
    if (item && item.data && item.data.length) {
      const result = [];
      // Stored first data in result and iterate from second value
      result.push(item.data[0]);
      for (let i = 1; i < item.data.length; i++) {
        const prev = item.data[i - 1];
        const cur = item.data[i];
        if (cur.x - prev.x > granularity * this.aggregationRange * this.maxAllowedGap) {
          result.push({ x: prev.x + 1, y: null });
        }
        result.push(cur);
      }
      item.data = result;
    }
  }
}
